import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const Section = styled.section`
  padding: 120px 80px;
  background: linear-gradient(to bottom, ${({ theme }) => theme.colors.darker}, ${({ theme }) => theme.colors.mid});

  @media (max-width: 900px) {
    padding: 80px 24px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
  max-width: 1360px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;

  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  max-width: 1360px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 60px 42px;
  @media (max-width: 600px) { padding: 36px 24px; }
  position: relative;
  overflow: hidden;
  transition: all 0.4s;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.gradients.surface};
    opacity: 0;
    transition: opacity 0.4s;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 0; height: 2px;
    background: ${({ theme }) => theme.gradients.brand};
    transition: width 0.5s ease;
  }

  &:hover {
    &::before { opacity: 1; }
    &::after { width: 100%; }
  }
`;

const Num = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  color: ${({ theme }) => theme.colors.blue};
  letter-spacing: 0.3em;
  margin-bottom: 32px;
  opacity: 0.8;
`;

const Icon = styled.div`
  font-size: 52px;
  margin-bottom: 26px;
  display: block;
  filter: drop-shadow(0 0 14px rgba(0,163,255,0.5));
  transition: transform 0.4s;

  ${Card}:hover & {
    transform: scale(1.1) translateY(-4px);
  }
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 46px;
  letter-spacing: 0.04em;
  margin-bottom: 22px;
  color: #fff;
`;

const CardDesc = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 15px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const pillarIcons = ['📖', '🌌', '⚡'];

export default function Pillars() {
  const { t } = useTranslation();
  const pillars = t('pillars.items', { returnObjects: true });

  return (
    <Section id="pilares">
      <Header>
        <AnimatedSection>
          <SectionLabel>{t('pillars.sectionLabel')}</SectionLabel>
          <Title>
            {t('pillars.titlePrefix')} <span className="accent">{t('pillars.titleAccent')}</span>
          </Title>
        </AnimatedSection>
      </Header>

      <Grid>
        {pillars.map((p, i) => (
          <AnimatedSection key={i} delay={i * 100}>
            <Card>
              <Num>{p.num}</Num>
              <Icon>{pillarIcons[i]}</Icon>
              <CardTitle>{p.title}</CardTitle>
              <CardDesc>{p.desc}</CardDesc>
            </Card>
          </AnimatedSection>
        ))}
      </Grid>
    </Section>
  );
}
